// @ts-strict-ignore
import { Button, Heading } from '@backend/lib-design-system';
import { ArrowDownFilledIcon } from '@backend/lib-design-system/icons';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { styled } from 'styled-components';

import { Link } from '~/components/Link';
import { useTranslation } from '~/hooks';
import { getMediaQuery } from '~/utils/responsive/media';

const StyledListGrid = styled.div<{ $maxColumns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $maxColumns }) => ($maxColumns > 3 ? 3 : $maxColumns || 3)}, 1fr);
  grid-auto-rows: min-content;
  gap: 12px;

  ${getMediaQuery('tablet')} {
    grid-template-columns: repeat(${({ $maxColumns }) => ($maxColumns > 2 ? 2 : $maxColumns || 2)}, 1fr);
  }

  ${getMediaQuery('mobile')} {
    grid-template-columns: 1fr;
  }
`;

export const StyledListGridItem = styled(Heading)<{ $display: boolean }>`
  color: #272356 !important;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }

  ${({ $display }) => ($display ? undefined : 'display: none;')}
`;

export const StyledListGridAnchor = styled(Link)`
  color: #272356 !important;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledListGridButton = styled(Button)`
  color: #4040b4;
  padding: 12px 8px;
`;

export type ListGridItem = { label: string; href: string };

type ListGridProps = {
  list: ListGridItem[];
  itemsPerPage?: number;
  maxColumns?: number;
};

export const ListGrid = ({ list, itemsPerPage = 500, maxColumns }: ListGridProps) => {
  const t = useTranslation();
  const [displayedPageIndex, setDisplayedPageIndex] = useState(0);

  const listPages: ListGridItem[][] = useMemo(() => {
    const listCopy = [...list];
    const result = [];
    while (listCopy.length) {
      result.push(listCopy.splice(0, itemsPerPage));
    }
    return result;
  }, [list, itemsPerPage]);

  const isDisplayed = useCallback((pageIndex: number) => pageIndex <= displayedPageIndex, [displayedPageIndex]);

  return (
    <>
      <StyledListGrid $maxColumns={maxColumns}>
        {listPages.map((page, pageIndex) => (
          <Fragment key={pageIndex}>
            {page.map((item, itemIndex) => (
              // a link in a list, not a section title: a heading per item would flood the page's outline
              <StyledListGridItem
                variant="heading-5"
                forwardedAs="div"
                key={itemIndex}
                $display={isDisplayed(pageIndex)}
              >
                <StyledListGridAnchor href={item.href}>{item.label}</StyledListGridAnchor>
              </StyledListGridItem>
            ))}
          </Fragment>
        ))}
      </StyledListGrid>
      {!isDisplayed(listPages.length - 1) && (
        <StyledListGridButton
          icon={ArrowDownFilledIcon}
          iconPlacement="right"
          onClick={() => setDisplayedPageIndex(displayedPageIndex + 1)}
          variant="transparent"
        >
          {t('buttons.load_more')}
        </StyledListGridButton>
      )}
    </>
  );
};
